/**
 * Browser-side image compression for upload flows.
 *
 * Added 2026-05-19 for the Manufacturing C4b purchase-bills upload —
 * phone-camera scans frequently arrive as 4-12 MB photos that compress
 * 5-10× with no readability loss for invoices. Saves R2 storage +
 * upload bandwidth + Worker-side decode time.
 *
 * Scope (deliberately tight):
 *   - JPEG / PNG / WebP — re-encoded via OffscreenCanvas
 *   - Other types (notably application/pdf) — passed through unchanged
 *   - Files under SKIPBELOWBYTES — passed through (overhead not worth it)
 *   - If re-encoded size ≥ original — keep original (some inputs are
 *     already near-optimally compressed)
 *
 * Uses native browser APIs (createImageBitmap + OffscreenCanvas) — no
 * external library, no bundle bloat. Works on all desktop browsers we
 * target for the operator dashboard.
 */

const MAXDIMENSION = 2000
const SKIPBELOWBYTES = 500 * 1024
const JPEGQUALITY = 0.85
const COMPRESSIBLETYPES = new Set<string>(['image/jpeg', 'image/png', 'image/webp'])

export interface CompressResult {
  /** Possibly the original; possibly a new File. */
  readonly file: File
  readonly compressed: boolean
  readonly originalSize: number
  readonly finalSize: number
}

/**
 * Pure predicate — true if the file is a candidate for compression
 * (right type + over the skip-below threshold). Exposed for testing.
 */
export function shouldCompress(file: { readonly size: number; readonly type: string }): boolean {
  if (!COMPRESSIBLETYPES.has(file.type)) return false
  if (file.size <= SKIPBELOWBYTES) return false
  return true
}

/**
 * Pure helper — given a source dimension pair and a max long edge,
 * returns the largest fitting dimensions while preserving aspect.
 * No upscaling: if both sides are already within max, returns them
 * unchanged.
 */
export function computeTargetSize(
  width: number,
  height: number,
  max: number
): { readonly width: number; readonly height: number } {
  if (width <= max && height <= max) return { width, height }
  if (width >= height) {
    return { width: max, height: Math.round((height / width) * max) }
  }
  return { width: Math.round((width / height) * max), height: max }
}

/**
 * Compress an image file if it's a candidate. Returns the original
 * File when the type isn't compressible, the file is too small,
 * decoding fails, or the re-encoded result would be larger than the
 * original (lossy re-encode of an already-small JPEG can paradoxically
 * grow).
 */
export async function compressImageIfApplicable(file: File): Promise<CompressResult> {
  const originalSize = file.size
  if (!shouldCompress(file)) {
    return { file, compressed: false, originalSize, finalSize: originalSize }
  }
  if (typeof OffscreenCanvas === 'undefined' || typeof createImageBitmap === 'undefined') {
    return { file, compressed: false, originalSize, finalSize: originalSize }
  }
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return { file, compressed: false, originalSize, finalSize: originalSize }
  }
  try {
    const { width, height } = computeTargetSize(bitmap.width, bitmap.height, MAXDIMENSION)
    const canvas = new OffscreenCanvas(width, height)
    const ctx = canvas.getContext('2d')
    if (ctx === null) {
      return { file, compressed: false, originalSize, finalSize: originalSize }
    }
    ctx.drawImage(bitmap, 0, 0, width, height)
    const opts: ImageEncodeOptions = { type: file.type }
    if (file.type === 'image/jpeg' || file.type === 'image/webp') {
      opts.quality = JPEGQUALITY
    }
    // PNG has no quality knob — the re-encode is lossless at the
    // (possibly smaller) target dimensions.
    const blob = await canvas.convertToBlob(opts)
    if (blob.size >= originalSize) {
      return { file, compressed: false, originalSize, finalSize: originalSize }
    }
    const newFile = new File([blob], file.name, { type: file.type })
    return { file: newFile, compressed: true, originalSize, finalSize: newFile.size }
  } finally {
    bitmap.close()
  }
}
